import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Form } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import { GlobalContext } from "../../../context/GlobalContext";
import axios from "../../../api/axios";
import helpers from "../../../utils/helpers";
import { CustomAddressAutocomplete } from "../../shared/CustomAddressAutoComplete";
import CoApplicantBirthday from "../../questionscomponent/subcomponent/personal/CoApplicantBirthday";

const EMPLOYMENT_STATUS_OPTIONS = [
  "Retired or Disabled",
  "Retired or Disabled and also currently employed",
  "Employed",
];
const HOUSING_STATUS_OPTIONS = ["Own", "Rent", "With Relatives", "With Friends"];
const ADDITIONAL_INCOME_SOURCE_OPTIONS = [
  "Social Security",
  "VA Disability",
  "Pension",
  "Retirement",
  "Rental Properties",
];
const CITIZENSHIP_OPTIONS = ["US Citizen", "Permanent Resident", "Resident Alien", "Other"];
const SUFFIX_OPTIONS = ["Jr.", "Sr.", "II", "III", "IV", "V"];
const MARITAL_STATUS_OPTIONS = ["Single", "Married", "Separated"];

// Maps this step's autosave POST body keys back to the local `errors`
// state keys that already have a render slot under their field — lets a
// backend validation rejection surface under the specific field instead of
// only as one generic message at the bottom of the form.
const SERVER_FIELD_ERROR_MAP = {
  ssn_number: "ssn",
  citizenship: "citizenship",
  personal_employment_status: "employmentStatus",
  personal_gross_monthly_income: "grossMonthlyIncome",
  personal_co_applicant_first_name: "coApplicantFirstName",
  personal_co_applicant_last_name: "coApplicantLastName",
  personal_co_applicant_email: "coApplicantEmail",
  personal_co_applicant_phone_no: "coApplicantPhone",
  personal_co_applicant_date_of_birth: "coApplicantDob",
  personal_co_applicant_employment_status: "coApplicantEmploymentStatus",
};

function formatPhoneNumber(digitsOnly) {
  if (!digitsOnly) return "";
  if (digitsOnly.length <= 3) return `(${digitsOnly}`;
  if (digitsOnly.length <= 6) return `(${digitsOnly.slice(0, 3)})-${digitsOnly.slice(3)}`;
  return `(${digitsOnly.slice(0, 3)})-${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
}

function formatSSN(sanitizedValue) {
  let formatted = "";
  for (let i = 0; i < sanitizedValue.length; i++) {
    formatted += i === 3 || i === 5 ? `-${sanitizedValue[i]}` : sanitizedValue[i];
  }
  return formatted;
}

// Shared by every dollar-amount field on this step (gross/retirement/other
// income, housing payment) — keeps the display value comma-formatted
// ("2,134") as the borrower types, same pattern as StepEquipment.js's price
// field.
function handleCurrencyChange(setter) {
  return (e) => {
    const cleaned = e.target.value.replace(/,/g, "").replace(/[^0-9]/g, "");
    const parsed = parseInt(cleaned);
    setter(isNaN(parsed) ? "" : parsed.toLocaleString());
  };
}

const isRetiredIsh = (status) =>
  status === "Retired or Disabled" || status === "Retired or Disabled and also currently employed";
const isWorking = (status) =>
  status === "Employed" || status === "Retired or Disabled and also currently employed";

/**
 * PD-6 Milestone 2 — personal path Step 3 "Employment & income".
 *
 * Merges personal/EmploymentStatus.js (chips), the employer reveal chain
 * (EmployerDetails/EmployerAddress), personal/RetirementDetails.js,
 * personal/EmploymentDetails.js (gross income), personal/MonthlyIncome-
 * equivalent, personal/AdditionalIncome.js, and personal/CoApplicant.js's
 * "do you have a co-applicant" toggle — expanded inline instead of
 * repeating the entire 16-screen co-applicant chain.
 *
 * Every field keeps its own individual `partial-application` autosave POST
 * call with the exact same payload shape as its original standalone screen.
 *
 * The co-applicant's suffix/marital status (from CoApplicantSuffix.js)
 * are included here too, immediately after date of birth — matching their
 * position in the old chain (CoApplicantSsn -> CoApplicantSuffix ->
 * CoApplicantResidentialAddress). The primary applicant's own suffix and
 * marital status live in StepResidence.js instead, since Suffix.js sat
 * immediately before ResidentialAddress.js in the old personal-path chain.
 */
const StepEmployment = forwardRef(({ goNext, goBack, isFirstStep, footNote }, ref) => {
  const { state, dispatch } = useContext(GlobalContext);
  const [showLoader, setShowLoader] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [serverError, setServerError] = useState("");

  // --- Primary applicant SSN & citizenship ---
  // Lives at the top level of state.questions (not nested under `personal`)
  // to match StepOwnership.js's business-flow equivalent and the backend's
  // shared required-field check (enforce_fields_validation_for_application),
  // which reads ssn_number/citizenship straight off the application record
  // regardless of flow.
  const [ssn, setSsn] = useState(() =>
    formatSSN((state?.questions?.ssn ?? "").replace(/\D/g, ""))
  );
  const [citizenship, setCitizenship] = useState(state?.questions?.citizenship ?? "");

  // --- Primary applicant employment/income ---
  const [employmentStatus, setEmploymentStatus] = useState(
    state?.questions?.personal?.employmentStatus ?? ""
  );
  const [businessName, setBusinessName] = useState(
    state?.questions?.personal?.businessName ?? ""
  );
  const [businessPhone, setBusinessPhone] = useState(() => {
    const stored = state?.questions?.personal?.businessPhone ?? "";
    return formatPhoneNumber(stored.replace("+1", "").replace(/[^0-9]/g, ""));
  });
  const [occupationTitle, setOccupationTitle] = useState(
    state?.questions?.personal?.occupationTitle ?? ""
  );
  const [employerAddress, setEmployerAddress] = useState(
    state?.questions?.personal?.employerAddress ?? ""
  );
  const [employerAddressComponents, setEmployerAddressComponents] = useState(
    JSON.parse(localStorage.getItem("employerComponentsMazo")) ?? []
  );
  const [employerAddressError, setEmployerAddressError] = useState("");
  const [employerUnitNumber, setEmployerUnitNumber] = useState(
    state?.questions?.personal?.employerUnitNumber ?? ""
  );

  const [retirementIncome, setRetirementIncome] = useState(
    state?.questions?.personal?.retirementIncome
      ? parseInt(state?.questions?.personal?.retirementIncome)?.toLocaleString()
      : ""
  );
  const [retirementYears, setRetirementYears] = useState(
    state?.questions?.personal?.retirementYears ?? ""
  );
  const [retirementMonths, setRetirementMonths] = useState(
    state?.questions?.personal?.retirementMonths ?? ""
  );

  const [grossMonthlyIncome, setGrossMonthlyIncome] = useState(
    state?.questions?.personal?.grossMonthlyIncome
      ? parseInt(state?.questions?.personal?.grossMonthlyIncome)?.toLocaleString()
      : ""
  );
  const [employmentYears, setEmploymentYears] = useState(
    state?.questions?.personal?.employmentYears ?? ""
  );
  const [employmentMonths, setEmploymentMonths] = useState(
    state?.questions?.personal?.employmentMonths ?? ""
  );

  const [additionalIncomeSource, setAdditionalIncomeSource] = useState(
    state?.questions?.personal?.additionalIncomeSource ?? ""
  );
  const [additionalMonthlyIncome, setAdditionalMonthlyIncome] = useState(
    state?.questions?.personal?.additionalMonthlyIncome
      ? parseInt(state?.questions?.personal?.additionalMonthlyIncome)?.toLocaleString()
      : ""
  );

  // --- Co-applicant toggle + fields ---
  const [isCoApplicant, setIsCoApplicant] = useState(
    Boolean(state?.questions?.personal?.isCoApplicant)
  );
  const [coApplicantFirstName, setCoApplicantFirstName] = useState(
    state?.questions?.personal?.coApplicantFirstName ?? ""
  );
  const [coApplicantLastName, setCoApplicantLastName] = useState(
    state?.questions?.personal?.coApplicantLastName ?? ""
  );
  const [coApplicantEmail, setCoApplicantEmail] = useState(
    state?.questions?.personal?.coApplicantEmail ?? ""
  );
  const [coApplicantPhone, setCoApplicantPhone] = useState(() => {
    const stored = state?.questions?.personal?.coApplicantPhone ?? "";
    return formatPhoneNumber(stored.replace("+1", "").replace(/[^0-9]/g, ""));
  });
  const [coApplicantSsn, setCoApplicantSsn] = useState(() =>
    formatSSN((state?.questions?.personal?.coApplicantSsn ?? "").replace(/\D/g, ""))
  );
  const [coApplicantCitizenship, setCoApplicantCitizenship] = useState(
    state?.questions?.personal?.coApplicantCitizenship ?? ""
  );
  const [coApplicantSuffix, setCoApplicantSuffix] = useState(
    state?.questions?.personal?.coApplicantSuffix ?? ""
  );
  const [coApplicantMaritalStatus, setCoApplicantMaritalStatus] = useState(
    state?.questions?.personal?.coApplicantMaritalStatus ?? ""
  );
  const [coApplicantResidentialAddress, setCoApplicantResidentialAddress] = useState(
    state?.questions?.personal?.coApplicantResidentialAddress ?? ""
  );
  const [coApplicantResidentialAddressComponents, setCoApplicantResidentialAddressComponents] =
    useState(JSON.parse(localStorage.getItem("coApplicantResidentialComponentsMazo")) ?? []);
  const [coApplicantResidentialAddressError, setCoApplicantResidentialAddressError] = useState("");
  const [coApplicantResidentialUnitNumber, setCoApplicantResidentialUnitNumber] = useState(
    state?.questions?.personal?.coApplicantResidentialUnitNumber ?? ""
  );

  const [coApplicantLivingYears, setCoApplicantLivingYears] = useState(
    state?.questions?.personal?.coApplicantLivingYears ?? ""
  );
  const [coApplicantLivingMonths, setCoApplicantLivingMonths] = useState(
    state?.questions?.personal?.coApplicantLivingMonths ?? ""
  );
  const [coApplicantLivingMonthlyPayment, setCoApplicantLivingMonthlyPayment] = useState(
    state?.questions?.personal?.coApplicantLivingMonthlyPayment
      ? parseInt(state?.questions?.personal?.coApplicantLivingMonthlyPayment)?.toLocaleString()
      : ""
  );
  const [coApplicantLivingStatus, setCoApplicantLivingStatus] = useState(
    state?.questions?.personal?.coApplicantLivingStatus ?? ""
  );

  const [coApplicantEmploymentStatus, setCoApplicantEmploymentStatus] = useState(
    state?.questions?.personal?.coApplicantEmploymentStatus ?? ""
  );
  const [coApplicantBusinessName, setCoApplicantBusinessName] = useState(
    state?.questions?.personal?.coApplicantBusinessName ?? ""
  );
  const [coApplicantGrossMonthlyIncome, setCoApplicantGrossMonthlyIncome] = useState(
    state?.questions?.personal?.coApplicantGrossMonthlyIncome
      ? parseInt(state?.questions?.personal?.coApplicantGrossMonthlyIncome)?.toLocaleString()
      : ""
  );
  const [coApplicantEmployerAddress, setCoApplicantEmployerAddress] = useState(
    state?.questions?.personal?.coApplicantEmployerAddress ?? ""
  );
  const [coApplicantEmployerAddressComponents, setCoApplicantEmployerAddressComponents] =
    useState(JSON.parse(localStorage.getItem("coApplicantEmployerComponentsMazo")) ?? []);
  const [coApplicantEmployerAddressError, setCoApplicantEmployerAddressError] = useState("");
  const [coApplicantEmployerUnitNumber, setCoApplicantEmployerUnitNumber] = useState(
    state?.questions?.personal?.coApplicantEmployerUnitNumber ?? ""
  );
  const [coApplicantRetirementIncome, setCoApplicantRetirementIncome] = useState(
    state?.questions?.personal?.coApplicantretirementIncome
      ? parseInt(state?.questions?.personal?.coApplicantretirementIncome)?.toLocaleString()
      : ""
  );
  const [coApplicantRetirementYears, setCoApplicantRetirementYears] = useState(
    state?.questions?.personal?.coApplicantretirementYears ?? ""
  );
  const [coApplicantRetirementMonths, setCoApplicantRetirementMonths] = useState(
    state?.questions?.personal?.coApplicantretirementMonths ?? ""
  );
  const [coApplicantEmploymentYears, setCoApplicantEmploymentYears] = useState(
    state?.questions?.personal?.coApplicantEmploymentYears ?? ""
  );
  const [coApplicantEmploymentMonths, setCoApplicantEmploymentMonths] = useState(
    state?.questions?.personal?.coApplicantEmploymentMonths ?? ""
  );
  const [coApplicantAdditionalIncomeSource, setCoApplicantAdditionalIncomeSource] = useState(
    state?.questions?.personal?.coApplicantAdditionalIncomeSource ?? ""
  );
  const [coApplicantAdditionalMonthlyIncome, setCoApplicantAdditionalMonthlyIncome] = useState(
    state?.questions?.personal?.coApplicantAdditionalMonthlyIncome
      ? parseInt(state?.questions?.personal?.coApplicantAdditionalMonthlyIncome)?.toLocaleString()
      : ""
  );

  // Selecting a suggestion fills in the address text immediately, but its
  // structured components (required for validation) only land once the
  // place-details lookup resolves. Track in-flight lookups so Continue can't
  // race ahead of them.
  const [pendingAddressLookups, setPendingAddressLookups] = useState(0);
  const handleAddressResolvingChange = (isResolving) => {
    setPendingAddressLookups((count) => count + (isResolving ? 1 : -1));
  };

  const [errors, setErrors] = useState({});

  // Same "just saved now" behavior as the personal-fields effect below, but
  // dispatched separately since ssn/citizenship live at the top level of
  // state.questions, not under state.questions.personal.
  useEffect(() => {
    dispatch({
      type: "QUESTIONS",
      payload: { ...state.questions, ssn: ssn.replace(/-/g, ""), citizenship },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssn, citizenship]);

  // "Just saved now": every field on this step (including the co-applicant
  // block) lands in global state as the borrower types/selects, not only
  // once they click Continue — so navigating Back then Continue again
  // restores whatever was filled in, instead of losing it because this
  // component's local state was discarded on unmount. Mirrors
  // handleContinue's own QUESTION_PERSONAL payload shape exactly, minus the
  // network calls, so the useState initializers above can read it straight
  // back. (Address autocomplete *components* already survive navigation
  // independently via localStorage — this only needs the search-string
  // fields.)
  useEffect(() => {
    const toInt = (value) => {
      const parsed = parseInt(String(value).replace(/,/g, ""));
      return isNaN(parsed) ? null : parsed;
    };
    dispatch({
      type: "QUESTION_PERSONAL",
      payload: {
        ...state.questions.personal,
        employmentStatus,
        businessName,
        businessPhone: businessPhone ? "+1" + businessPhone.replace(/[^0-9]/g, "") : "",
        occupationTitle,
        employerAddress,
        employerUnitNumber,
        retirementIncome: toInt(retirementIncome),
        retirementYears,
        retirementMonths,
        grossMonthlyIncome: toInt(grossMonthlyIncome),
        employmentYears,
        employmentMonths,
        additionalIncomeSource,
        additionalMonthlyIncome: toInt(additionalMonthlyIncome),
        isCoApplicant,
        // Matches handleContinue's own ternary below — turning the
        // co-applicant toggle off means "no co-applicant", same as a real
        // submit, so their fields clear immediately rather than lingering
        // as stale data the borrower already said isn't relevant.
        coApplicantFirstName: isCoApplicant ? coApplicantFirstName : "",
        coApplicantLastName: isCoApplicant ? coApplicantLastName : "",
        coApplicantEmail: isCoApplicant ? coApplicantEmail : "",
        coApplicantPhone: isCoApplicant
          ? coApplicantPhone
            ? "+1" + coApplicantPhone.replace(/[^0-9]/g, "")
            : "+1"
          : "+1",
        coApplicantSsn: isCoApplicant ? coApplicantSsn.replace(/-/g, "") : "",
        coApplicantCitizenship: isCoApplicant ? coApplicantCitizenship : "",
        coApplicantSuffix: isCoApplicant ? coApplicantSuffix : "",
        coApplicantMaritalStatus: isCoApplicant ? coApplicantMaritalStatus : "",
        coApplicantResidentialAddress: isCoApplicant ? coApplicantResidentialAddress : "",
        coApplicantResidentialUnitNumber: isCoApplicant ? coApplicantResidentialUnitNumber : "",
        coApplicantLivingYears: isCoApplicant ? coApplicantLivingYears : "",
        coApplicantLivingMonths: isCoApplicant ? coApplicantLivingMonths : "",
        coApplicantLivingMonthlyPayment: isCoApplicant ? toInt(coApplicantLivingMonthlyPayment) : null,
        coApplicantLivingStatus: isCoApplicant ? coApplicantLivingStatus : "",
        coApplicantEmploymentStatus: isCoApplicant ? coApplicantEmploymentStatus : "",
        coApplicantBusinessName: isCoApplicant ? coApplicantBusinessName : "",
        coApplicantGrossMonthlyIncome: isCoApplicant ? toInt(coApplicantGrossMonthlyIncome) : null,
        coApplicantEmployerAddress: isCoApplicant ? coApplicantEmployerAddress : "",
        coApplicantEmployerUnitNumber: isCoApplicant ? coApplicantEmployerUnitNumber : "",
        coApplicantretirementIncome: isCoApplicant ? toInt(coApplicantRetirementIncome) : null,
        coApplicantretirementYears: isCoApplicant ? coApplicantRetirementYears : "",
        coApplicantretirementMonths: isCoApplicant ? coApplicantRetirementMonths : "",
        coApplicantEmploymentYears: isCoApplicant ? coApplicantEmploymentYears : "",
        coApplicantEmploymentMonths: isCoApplicant ? coApplicantEmploymentMonths : "",
        coApplicantAdditionalIncomeSource: isCoApplicant ? coApplicantAdditionalIncomeSource : "",
        coApplicantAdditionalMonthlyIncome: isCoApplicant
          ? toInt(coApplicantAdditionalMonthlyIncome)
          : null,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    employmentStatus,
    businessName,
    businessPhone,
    occupationTitle,
    employerAddress,
    employerUnitNumber,
    retirementIncome,
    retirementYears,
    retirementMonths,
    grossMonthlyIncome,
    employmentYears,
    employmentMonths,
    additionalIncomeSource,
    additionalMonthlyIncome,
    isCoApplicant,
    coApplicantFirstName,
    coApplicantLastName,
    coApplicantEmail,
    coApplicantPhone,
    coApplicantSsn,
    coApplicantCitizenship,
    coApplicantSuffix,
    coApplicantMaritalStatus,
    coApplicantResidentialAddress,
    coApplicantResidentialUnitNumber,
    coApplicantLivingYears,
    coApplicantLivingMonths,
    coApplicantLivingMonthlyPayment,
    coApplicantLivingStatus,
    coApplicantEmploymentStatus,
    coApplicantBusinessName,
    coApplicantGrossMonthlyIncome,
    coApplicantEmployerAddress,
    coApplicantEmployerUnitNumber,
    coApplicantRetirementIncome,
    coApplicantRetirementYears,
    coApplicantRetirementMonths,
    coApplicantEmploymentYears,
    coApplicantEmploymentMonths,
    coApplicantAdditionalIncomeSource,
    coApplicantAdditionalMonthlyIncome,
  ]);

  // UI-only, not sent to backend — "Total monthly" is a computed, read-only
  // display of gross monthly income plus other (additional) income only.
  const totalMonthlyIncome =
    (parseInt(String(grossMonthlyIncome).replace(/,/g, "")) || 0) +
    (parseInt(String(additionalMonthlyIncome).replace(/,/g, "")) || 0);

  const validate = () => {
    const validationErrors = {};
    if (!ssn || !/^\d{3}-\d{2}-\d{4}$/.test(ssn)) {
      validationErrors.ssn = "Please provide a valid SSN e.g. (AAA-GG-SSSS)";
    }
    if (!citizenship) validationErrors.citizenship = "Please select an option.";
    if (!employmentStatus) validationErrors.employmentStatus = "Please select an option.";
    if (grossMonthlyIncome === "" || grossMonthlyIncome === null) {
      validationErrors.grossMonthlyIncome = "This field is required.";
    }
    if (isCoApplicant) {
      if (!coApplicantFirstName || coApplicantFirstName.trim().length < 3) {
        validationErrors.coApplicantFirstName = "Please enter a valid first name (min 3 characters).";
      }
      if (!coApplicantLastName || coApplicantLastName.trim().length < 3) {
        validationErrors.coApplicantLastName = "Please enter a valid last name (min 3 characters).";
      }
      if (!coApplicantEmail) validationErrors.coApplicantEmail = "Please provide a valid email.";
      // The backend requires the co-applicant's phone to create their
      // record at final submission — enforcing it here catches it before
      // Continue instead of a late "Missing required fields" at sign & submit.
      if (!coApplicantPhone || !/^\(\d{3}\)-\d{3}-\d{4}$/.test(coApplicantPhone)) {
        validationErrors.coApplicantPhone = "Please provide a valid US phone number e.g. (234)-567-8900";
      }
      if (!coApplicantEmploymentStatus) {
        validationErrors.coApplicantEmploymentStatus = "Please select an option.";
      }
    }
    return validationErrors;
  };

  useImperativeHandle(ref, () => ({
    validateForSaveLater: () => {
      const validationErrors = validate();
      setErrors(validationErrors);
      setSubmitAttempted(true);
      return Object.keys(validationErrors).length === 0;
    },
  }));

  // Re-run validation live once the borrower has tried to submit once, so a
  // field's error clears as soon as it becomes valid instead of waiting for
  // another Continue press.
  useEffect(() => {
    if (submitAttempted) setErrors(validate());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    submitAttempted,
    ssn,
    citizenship,
    employmentStatus,
    grossMonthlyIncome,
    isCoApplicant,
    coApplicantFirstName,
    coApplicantLastName,
    coApplicantEmail,
    coApplicantPhone,
    coApplicantEmploymentStatus,
  ]);

  const handleContinue = async (e) => {
    e.preventDefault();
    if (pendingAddressLookups > 0) return;
    setSubmitAttempted(true);
    setServerError("");

    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const sanitizedSsn = ssn.replace(/-/g, "");
    dispatch({
      type: "QUESTIONS",
      payload: { ...state.questions, ssn: sanitizedSsn, citizenship },
    });

    const updatedBusinessPhone = businessPhone ? "+1" + businessPhone.replace(/[^0-9]/g, "") : "";
    const updatedCoApplicantPhone = coApplicantPhone
      ? "+1" + coApplicantPhone.replace(/[^0-9]/g, "")
      : "";
    const sanitizedCoApplicantSsn = coApplicantSsn.replace(/-/g, "");

    // Currency fields hold a comma-formatted display string (e.g. "2,134") —
    // strip commas back to a plain integer before this goes anywhere near
    // dispatch or the backend.
    const toInt = (value) => {
      const parsed = parseInt(String(value).replace(/,/g, ""));
      return isNaN(parsed) ? null : parsed;
    };
    const sanitizedRetirementIncome = toInt(retirementIncome);
    const sanitizedGrossMonthlyIncome = toInt(grossMonthlyIncome);
    const sanitizedAdditionalMonthlyIncome = toInt(additionalMonthlyIncome);
    const sanitizedCoApplicantLivingMonthlyPayment = toInt(coApplicantLivingMonthlyPayment);
    const sanitizedCoApplicantGrossMonthlyIncome = toInt(coApplicantGrossMonthlyIncome);
    const sanitizedCoApplicantRetirementIncome = toInt(coApplicantRetirementIncome);
    const sanitizedCoApplicantAdditionalMonthlyIncome = toInt(coApplicantAdditionalMonthlyIncome);

    dispatch({
      type: "QUESTION_PERSONAL",
      payload: {
        ...state.questions.personal,
        employmentStatus,
        businessName,
        businessPhone: updatedBusinessPhone,
        occupationTitle,
        employerAddress,
        employerUnitNumber,
        retirementIncome: sanitizedRetirementIncome,
        retirementYears,
        retirementMonths,
        grossMonthlyIncome: sanitizedGrossMonthlyIncome,
        employmentYears,
        employmentMonths,
        additionalIncomeSource,
        additionalMonthlyIncome: sanitizedAdditionalMonthlyIncome,
        isCoApplicant,
        coApplicantFirstName: isCoApplicant ? coApplicantFirstName : "",
        coApplicantLastName: isCoApplicant ? coApplicantLastName : "",
        coApplicantEmail: isCoApplicant ? coApplicantEmail : "",
        coApplicantPhone: isCoApplicant ? updatedCoApplicantPhone : "+1",
        coApplicantSsn: isCoApplicant ? sanitizedCoApplicantSsn : "",
        coApplicantCitizenship: isCoApplicant ? coApplicantCitizenship : "",
        coApplicantSuffix: isCoApplicant ? coApplicantSuffix : "",
        coApplicantMaritalStatus: isCoApplicant ? coApplicantMaritalStatus : "",
        coApplicantResidentialAddress: isCoApplicant ? coApplicantResidentialAddress : "",
        coApplicantResidentialUnitNumber: isCoApplicant ? coApplicantResidentialUnitNumber : "",
        coApplicantLivingYears: isCoApplicant ? coApplicantLivingYears : "",
        coApplicantLivingMonths: isCoApplicant ? coApplicantLivingMonths : "",
        coApplicantLivingMonthlyPayment: isCoApplicant
          ? sanitizedCoApplicantLivingMonthlyPayment
          : null,
        coApplicantLivingStatus: isCoApplicant ? coApplicantLivingStatus : "",
        coApplicantEmploymentStatus: isCoApplicant ? coApplicantEmploymentStatus : "",
        coApplicantBusinessName: isCoApplicant ? coApplicantBusinessName : "",
        coApplicantGrossMonthlyIncome: isCoApplicant
          ? sanitizedCoApplicantGrossMonthlyIncome
          : null,
        coApplicantEmployerAddress: isCoApplicant ? coApplicantEmployerAddress : "",
        coApplicantEmployerUnitNumber: isCoApplicant ? coApplicantEmployerUnitNumber : "",
        coApplicantretirementIncome: isCoApplicant ? sanitizedCoApplicantRetirementIncome : null,
        coApplicantretirementYears: isCoApplicant ? coApplicantRetirementYears : "",
        coApplicantretirementMonths: isCoApplicant ? coApplicantRetirementMonths : "",
        coApplicantEmploymentYears: isCoApplicant ? coApplicantEmploymentYears : "",
        coApplicantEmploymentMonths: isCoApplicant ? coApplicantEmploymentMonths : "",
        coApplicantAdditionalIncomeSource: isCoApplicant ? coApplicantAdditionalIncomeSource : "",
        coApplicantAdditionalMonthlyIncome: isCoApplicant
          ? sanitizedCoApplicantAdditionalMonthlyIncome
          : null,
      },
    });

    const endpoint = `lender/partial-application/${state?.informationSlug}/?uuid=${state?.applicationUUID}`;

    setShowLoader(true);
    try {
      // Each field/group keeps its own autosave call, matching the payload
      // shape of its original standalone screen. None of these calls reads
      // a response from a preceding call, so they are independent and can
      // fire concurrently instead of one round trip at a time. Conditional
      // groups (employer/retirement reveals, co-applicant block and its own
      // nested reveals) are only pushed onto the batch when their condition
      // holds, exactly as they were only awaited before.
      const requests = [
        axios.post(endpoint, { ssn_number: sanitizedSsn, citizenship }),
        axios.post(endpoint, { personal_employment_status: employmentStatus }),
      ];

      if (isWorking(employmentStatus)) {
        requests.push(
          axios.post(endpoint, {
            personal_employer_business_name: businessName,
            personal_employer_business_phone_no: updatedBusinessPhone,
            personal_occupation_title: occupationTitle,
          })
        );
        const employer = helpers.extractAddressComponents(
          employerAddressComponents,
          { state: "short", route: "short" },
          employerUnitNumber
        );
        const employerBody = {};
        if (employer !== false) employerBody.personal_employer_address = employer;
        requests.push(axios.post(endpoint, { body: employerBody }));
      }

      if (isRetiredIsh(employmentStatus)) {
        requests.push(
          axios.post(endpoint, {
            personal_retirement_income: sanitizedRetirementIncome,
            personal_retirement_years: retirementYears,
            personal_retirement_months: retirementMonths,
          })
        );
      }

      requests.push(
        axios.post(endpoint, {
          personal_gross_monthly_income: sanitizedGrossMonthlyIncome,
          personal_employment_years: employmentYears,
          personal_employment_months: employmentMonths,
        })
      );

      requests.push(
        axios.post(endpoint, {
          personal_additional_income_source: additionalIncomeSource,
          personal_additional_income_monthly: sanitizedAdditionalMonthlyIncome,
        })
      );

      requests.push(axios.post(endpoint, { is_co_applicant: isCoApplicant }));

      if (isCoApplicant) {
        requests.push(
          axios.post(endpoint, { personal_co_applicant_first_name: coApplicantFirstName }),
          axios.post(endpoint, { personal_co_applicant_last_name: coApplicantLastName }),
          axios.post(endpoint, { personal_co_applicant_email: coApplicantEmail }),
          axios.post(endpoint, { personal_co_applicant_phone_no: updatedCoApplicantPhone }),
          axios.post(endpoint, {
            personal_co_applicant_ssn: sanitizedCoApplicantSsn,
            personal_co_applicant_citizenship: coApplicantCitizenship,
          }),
          axios.post(endpoint, {
            // Co-applicant DOB is optional — send null, not "", when it was
            // never picked. The backend's DateField rejects "" outright
            // ("Date has wrong format"), it only accepts a real date or null.
            personal_co_applicant_date_of_birth: state?.questions?.personal?.coApplicantDob || null,
          }),
          axios.post(endpoint, {
            personal_co_applicant_suffix: coApplicantSuffix,
            personal_co_applicant_marital_status: coApplicantMaritalStatus,
          })
        );

        const coApplicantHome = helpers.extractAddressComponents(
          coApplicantResidentialAddressComponents,
          { state: "short", route: "short" },
          coApplicantResidentialUnitNumber
        );
        const coApplicantHomeBody = {};
        if (coApplicantHome !== false) {
          coApplicantHomeBody.personal_co_applicant_home_address = coApplicantHome;
        }
        requests.push(axios.post(endpoint, { body: coApplicantHomeBody }));

        requests.push(
          axios.post(endpoint, {
            personal_co_applicant_living_years: coApplicantLivingYears,
            personal_co_applicant_living_months: coApplicantLivingMonths,
            personal_co_applicant_monthly_payment: sanitizedCoApplicantLivingMonthlyPayment,
            personal_co_applicant_living_status: coApplicantLivingStatus,
          })
        );

        requests.push(
          axios.post(endpoint, {
            personal_co_applicant_employment_status: coApplicantEmploymentStatus,
          })
        );

        if (isWorking(coApplicantEmploymentStatus)) {
          requests.push(
            axios.post(endpoint, {
              personal_co_applicant_employer_business_name: coApplicantBusinessName,
              personal_co_applicant_gross_monthly_income: sanitizedCoApplicantGrossMonthlyIncome,
            })
          );
          const coApplicantEmployer = helpers.extractAddressComponents(
            coApplicantEmployerAddressComponents,
            { state: "short", route: "short" },
            coApplicantEmployerUnitNumber
          );
          const coApplicantEmployerBody = {};
          if (coApplicantEmployer !== false) {
            coApplicantEmployerBody.personal_co_applicant_employer_address = coApplicantEmployer;
          }
          requests.push(axios.post(endpoint, { body: coApplicantEmployerBody }));
        }

        if (isRetiredIsh(coApplicantEmploymentStatus)) {
          requests.push(
            axios.post(endpoint, {
              personal_co_applicant_retirement_income: sanitizedCoApplicantRetirementIncome,
              personal_co_applicant_retirement_years: coApplicantRetirementYears,
              personal_co_applicant_retirement_months: coApplicantRetirementMonths,
            })
          );
        }

        requests.push(
          axios.post(endpoint, {
            personal_co_applicant_employment_years: coApplicantEmploymentYears,
            personal_co_applicant_employment_months: coApplicantEmploymentMonths,
          })
        );

        requests.push(
          axios.post(endpoint, {
            personal_co_applicant_additional_income_source: coApplicantAdditionalIncomeSource,
            personal_co_applicant_additional_income_monthly: sanitizedCoApplicantAdditionalMonthlyIncome,
          })
        );
      }

      await Promise.all(requests);
      goNext();
    } catch (error) {
      const parsed = helpers.parseServerFieldErrors(error?.response?.data, SERVER_FIELD_ERROR_MAP);
      if (parsed) {
        setErrors((prev) => ({ ...prev, ...parsed.fieldErrors }));
        setServerError(parsed.message);
      } else {
        setServerError(
          error?.response?.data?.message || "An error occurred. Please try again."
        );
      }
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <div className="pd6-step">
      <h2 className="pd6-step__title">Employment &amp; income</h2>
      <p className="pd6-step__helper">
        Pick your status and only the relevant fields appear.
      </p>

      <Form noValidate onSubmit={handleContinue} className="pd6-form">
        <div className="pd6-field-row">
          <Form.Group className="pd6-field" controlId="stepEmploymentSsn">
            <Form.Label>
              SSN<span className="pd6-required">*</span>
            </Form.Label>
            <Form.Control
              type="tel"
              inputMode="numeric"
              placeholder="123-45-6789"
              value={ssn}
              onChange={(e) => {
                const sanitized = e.target.value.replace(/\D/g, "");
                if (sanitized.length < 10) setSsn(formatSSN(sanitized));
              }}
              isInvalid={submitAttempted && Boolean(errors.ssn)}
            />
            <Form.Control.Feedback type="invalid">{errors.ssn}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="pd6-field" controlId="stepEmploymentCitizenship">
            <Form.Label>
              Citizenship<span className="pd6-required">*</span>
            </Form.Label>
            <div className="pd6-chip-group">
              {CITIZENSHIP_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`pd6-chip ${citizenship === option ? "pd6-chip--selected" : ""}`}
                  onClick={() => setCitizenship(option)}
                >
                  {option}
                </button>
              ))}
            </div>
            {submitAttempted && errors.citizenship && (
              <div className="pd6-field-error">{errors.citizenship}</div>
            )}
          </Form.Group>
        </div>

        <Form.Group className="pd6-field" controlId="stepEmploymentStatus">
          <Form.Label>
            Employment status<span className="pd6-required">*</span>
          </Form.Label>
          <div className="pd6-chip-group">
            {EMPLOYMENT_STATUS_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={`pd6-chip ${employmentStatus === option ? "pd6-chip--selected" : ""}`}
                onClick={() => setEmploymentStatus(option)}
              >
                {option}
              </button>
            ))}
          </div>
          {submitAttempted && errors.employmentStatus && (
            <div className="pd6-field-error">{errors.employmentStatus}</div>
          )}
        </Form.Group>

        {isWorking(employmentStatus) && (
          <div className="pd6-reveal-panel">
            <span className="pd6-reveal-panel__tag">Employer details</span>
            <div className="pd6-field-row mt-3">
              <Form.Group className="pd6-field" controlId="stepEmploymentBusinessName">
                <Form.Label>Business name</Form.Label>
                <Form.Control
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="pd6-field" controlId="stepEmploymentOccupation">
                <Form.Label>Occupation title</Form.Label>
                <Form.Control
                  type="text"
                  value={occupationTitle}
                  onChange={(e) => setOccupationTitle(e.target.value)}
                />
              </Form.Group>
            </div>
            <Form.Group className="pd6-field" controlId="stepEmploymentBusinessPhone">
              <Form.Label>Business phone</Form.Label>
              <Form.Control
                type="tel"
                inputMode="numeric"
                placeholder="(555)-555-5555"
                value={businessPhone}
                onChange={(e) => {
                  const digits = e.target.value.replace(/[^0-9]/g, "");
                  if (digits.length <= 10) setBusinessPhone(formatPhoneNumber(digits));
                }}
              />
            </Form.Group>
            <Form.Group className="pd6-field" controlId="stepEmploymentEmployerAddress">
              <CustomAddressAutocomplete
                label="Employer address"
                addressSearchQuery={employerAddress}
                setAddressSearchQuery={setEmployerAddress}
                setAddressComponents={setEmployerAddressComponents}
                addressValidationError={employerAddressError}
                setAddressValidationError={setEmployerAddressError}
                placeholder="employer address"
                localStorageLabel="employerComponentsMazo"
                isRequired={false}
                onResolvingChange={handleAddressResolvingChange}
              />
              <Form.Control
                type="text"
                className="pd6-address-unit-input"
                placeholder="Apt, suite, or unit (optional)"
                autoComplete="off"
                value={employerUnitNumber}
                onChange={(e) => setEmployerUnitNumber(e.target.value)}
              />
            </Form.Group>
          </div>
        )}

        {isRetiredIsh(employmentStatus) && (
          <div className="pd6-reveal-panel">
            <span className="pd6-reveal-panel__tag">Retirement details</span>
            <div className="pd6-field-row mt-3">
              <Form.Group className="pd6-field" controlId="stepEmploymentRetirementIncome">
                <Form.Label>Retirement income</Form.Label>
                <div className="pd6-currency-input">
                  <span className="pd6-currency-input__prefix">$</span>
                  <Form.Control
                    type="text"
                    placeholder="0"
                    value={retirementIncome}
                    onChange={handleCurrencyChange(setRetirementIncome)}
                  />
                </div>
              </Form.Group>
            </div>
            <div className="pd6-field-row">
              <Form.Group className="pd6-field" controlId="stepEmploymentRetirementYears">
                <Form.Label>Years</Form.Label>
                <Form.Control
                  type="text"
                  value={retirementYears}
                  onChange={(e) => setRetirementYears(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="pd6-field" controlId="stepEmploymentRetirementMonths">
                <Form.Label>Months</Form.Label>
                <Form.Control
                  type="text"
                  value={retirementMonths}
                  onChange={(e) => setRetirementMonths(e.target.value)}
                />
              </Form.Group>
            </div>
          </div>
        )}

        <div className="pd6-field-row pd6-field-row--triple">
          <Form.Group className="pd6-field" controlId="stepEmploymentGrossIncome">
            <Form.Label>
              Gross monthly income<span className="pd6-required">*</span>
            </Form.Label>
            <div className="pd6-currency-input">
              <span className="pd6-currency-input__prefix">$</span>
              <Form.Control
                type="text"
                placeholder="0"
                value={grossMonthlyIncome}
                onChange={handleCurrencyChange(setGrossMonthlyIncome)}
                isInvalid={submitAttempted && Boolean(errors.grossMonthlyIncome)}
              />
            </div>
            {submitAttempted && errors.grossMonthlyIncome && (
              <div className="pd6-field-error">{errors.grossMonthlyIncome}</div>
            )}
          </Form.Group>

          <Form.Group className="pd6-field" controlId="stepEmploymentAdditionalIncome">
            <Form.Label>Other income</Form.Label>
            <div className="pd6-chip-group">
              {ADDITIONAL_INCOME_SOURCE_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`pd6-chip ${additionalIncomeSource === option ? "pd6-chip--selected" : ""}`}
                  onClick={() => setAdditionalIncomeSource(option)}
                >
                  {option}
                </button>
              ))}
            </div>
            <div className="pd6-currency-input" style={{ marginTop: "8px" }}>
              <span className="pd6-currency-input__prefix">$</span>
              <Form.Control
                type="text"
                placeholder="0"
                value={additionalMonthlyIncome}
                onChange={handleCurrencyChange(setAdditionalMonthlyIncome)}
              />
            </div>
          </Form.Group>

          <Form.Group className="pd6-field" controlId="stepEmploymentTotalIncome">
            <Form.Label>Total monthly</Form.Label>
            <div className="pd6-currency-input pd6-currency-input--readonly">
              <span className="pd6-currency-input__prefix">$</span>
              <Form.Control
                type="text"
                value={totalMonthlyIncome.toLocaleString()}
                readOnly
                disabled
                tabIndex={-1}
              />
            </div>
          </Form.Group>
        </div>

        <div className="pd6-field-row">
          <Form.Group className="pd6-field" controlId="stepEmploymentYears">
            <Form.Label>Years employed</Form.Label>
            <Form.Control
              type="text"
              value={employmentYears}
              onChange={(e) => setEmploymentYears(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="pd6-field" controlId="stepEmploymentMonths">
            <Form.Label>Months employed</Form.Label>
            <Form.Control
              type="text"
              value={employmentMonths}
              onChange={(e) => setEmploymentMonths(e.target.value)}
            />
          </Form.Group>
        </div>

        <Form.Group className="pd6-field" controlId="stepEmploymentCoApplicant">
          <div className="pd6-toggle-row">
            <Form.Label className="pd6-toggle-row__label">Adding a co-applicant</Form.Label>
            <Form.Check
              type="switch"
              id="stepEmploymentCoApplicantSwitch"
              className="pd6-toggle"
              checked={isCoApplicant}
              onChange={(e) => setIsCoApplicant(e.target.checked)}
              label=""
            />
          </div>
        </Form.Group>

        {isCoApplicant && (
          <div className="pd6-reveal-panel">
            <span className="pd6-reveal-panel__tag">Co-applicant</span>

            <div className="pd6-field-row mt-3">
              <Form.Group className="pd6-field" controlId="stepCoApplicantFirstName">
                <Form.Label>
                  First name<span className="pd6-required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={coApplicantFirstName}
                  onChange={(e) => setCoApplicantFirstName(e.target.value)}
                  isInvalid={submitAttempted && Boolean(errors.coApplicantFirstName)}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.coApplicantFirstName}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="pd6-field" controlId="stepCoApplicantLastName">
                <Form.Label>
                  Last name<span className="pd6-required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={coApplicantLastName}
                  onChange={(e) => setCoApplicantLastName(e.target.value)}
                  isInvalid={submitAttempted && Boolean(errors.coApplicantLastName)}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.coApplicantLastName}
                </Form.Control.Feedback>
              </Form.Group>
            </div>

            <div className="pd6-field-row">
              <Form.Group className="pd6-field" controlId="stepCoApplicantEmail">
                <Form.Label>
                  Email<span className="pd6-required">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  value={coApplicantEmail}
                  onChange={(e) => setCoApplicantEmail(e.target.value)}
                  isInvalid={submitAttempted && Boolean(errors.coApplicantEmail)}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.coApplicantEmail}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="pd6-field" controlId="stepCoApplicantPhone">
                <Form.Label>
                  Phone<span className="pd6-required">*</span>
                </Form.Label>
                <Form.Control
                  type="tel"
                  inputMode="numeric"
                  placeholder="(555)-555-5555"
                  value={coApplicantPhone}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/[^0-9]/g, "");
                    if (digits.length <= 10) setCoApplicantPhone(formatPhoneNumber(digits));
                  }}
                  isInvalid={submitAttempted && Boolean(errors.coApplicantPhone)}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.coApplicantPhone}
                </Form.Control.Feedback>
              </Form.Group>
            </div>

            <div className="pd6-field-row">
              <Form.Group className="pd6-field" controlId="stepCoApplicantSsn">
                <Form.Label>SSN</Form.Label>
                <Form.Control
                  type="tel"
                  inputMode="numeric"
                  placeholder="123-45-6789"
                  value={coApplicantSsn}
                  onChange={(e) => {
                    const sanitized = e.target.value.replace(/\D/g, "");
                    if (sanitized.length < 10) setCoApplicantSsn(formatSSN(sanitized));
                  }}
                />
              </Form.Group>
              <Form.Group className="pd6-field" controlId="stepCoApplicantCitizenship">
                <Form.Label>Citizenship</Form.Label>
                <div className="pd6-chip-group">
                  {CITIZENSHIP_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`pd6-chip ${
                        coApplicantCitizenship === option ? "pd6-chip--selected" : ""
                      }`}
                      onClick={() => setCoApplicantCitizenship(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </Form.Group>
            </div>

            <Form.Group className="pd6-field" controlId="stepCoApplicantDob">
              <Form.Label>Date of birth</Form.Label>
              <CoApplicantBirthday />
              {submitAttempted && errors.coApplicantDob && (
                <div className="pd6-field-error">{errors.coApplicantDob}</div>
              )}
            </Form.Group>

            <div className="pd6-field-row">
              <Form.Group className="pd6-field" controlId="stepCoApplicantSuffix">
                <Form.Label>Suffix</Form.Label>
                <div className="pd6-chip-group">
                  {SUFFIX_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`pd6-chip ${coApplicantSuffix === option ? "pd6-chip--selected" : ""}`}
                      onClick={() => setCoApplicantSuffix(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </Form.Group>
              <Form.Group className="pd6-field" controlId="stepCoApplicantMaritalStatus">
                <Form.Label>Marital status</Form.Label>
                <div className="pd6-chip-group">
                  {MARITAL_STATUS_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`pd6-chip ${
                        coApplicantMaritalStatus === option ? "pd6-chip--selected" : ""
                      }`}
                      onClick={() => setCoApplicantMaritalStatus(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </Form.Group>
            </div>

            <Form.Group className="pd6-field" controlId="stepCoApplicantResidentialAddress">
              <CustomAddressAutocomplete
                label="Residential address"
                addressSearchQuery={coApplicantResidentialAddress}
                setAddressSearchQuery={setCoApplicantResidentialAddress}
                setAddressComponents={setCoApplicantResidentialAddressComponents}
                addressValidationError={coApplicantResidentialAddressError}
                setAddressValidationError={setCoApplicantResidentialAddressError}
                placeholder="residential address"
                localStorageLabel="coApplicantResidentialComponentsMazo"
                isRequired={false}
                onResolvingChange={handleAddressResolvingChange}
              />
              <Form.Control
                type="text"
                className="pd6-address-unit-input"
                placeholder="Apt, suite, or unit (optional)"
                autoComplete="off"
                value={coApplicantResidentialUnitNumber}
                onChange={(e) => setCoApplicantResidentialUnitNumber(e.target.value)}
              />
            </Form.Group>

            <div className="pd6-field-row">
              <Form.Group className="pd6-field" controlId="stepCoApplicantLivingYears">
                <Form.Label>Years at address</Form.Label>
                <Form.Control
                  type="text"
                  value={coApplicantLivingYears}
                  onChange={(e) => setCoApplicantLivingYears(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="pd6-field" controlId="stepCoApplicantLivingMonths">
                <Form.Label>Months at address</Form.Label>
                <Form.Control
                  type="text"
                  value={coApplicantLivingMonths}
                  onChange={(e) => setCoApplicantLivingMonths(e.target.value)}
                />
              </Form.Group>
            </div>

            <div className="pd6-field-row">
              <Form.Group className="pd6-field" controlId="stepCoApplicantMonthlyPayment">
                <Form.Label>Monthly housing payment</Form.Label>
                <div className="pd6-currency-input">
                  <span className="pd6-currency-input__prefix">$</span>
                  <Form.Control
                    type="text"
                    placeholder="0"
                    value={coApplicantLivingMonthlyPayment}
                    onChange={handleCurrencyChange(setCoApplicantLivingMonthlyPayment)}
                  />
                </div>
              </Form.Group>
              <Form.Group className="pd6-field" controlId="stepCoApplicantHousingStatus">
                <Form.Label>Housing status</Form.Label>
                <div className="pd6-chip-group pd6-chip-group--bands">
                  {HOUSING_STATUS_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`pd6-chip pd6-chip--band ${
                        coApplicantLivingStatus === option ? "pd6-chip--selected" : ""
                      }`}
                      onClick={() => setCoApplicantLivingStatus(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </Form.Group>
            </div>

            <Form.Group className="pd6-field" controlId="stepCoApplicantEmploymentStatus">
              <Form.Label>
                Employment status<span className="pd6-required">*</span>
              </Form.Label>
              <div className="pd6-chip-group">
                {EMPLOYMENT_STATUS_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`pd6-chip ${
                      coApplicantEmploymentStatus === option ? "pd6-chip--selected" : ""
                    }`}
                    onClick={() => setCoApplicantEmploymentStatus(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
              {submitAttempted && errors.coApplicantEmploymentStatus && (
                <div className="pd6-field-error">{errors.coApplicantEmploymentStatus}</div>
              )}
            </Form.Group>

            {isWorking(coApplicantEmploymentStatus) && (
              <div className="pd6-reveal-panel">
                <span className="pd6-reveal-panel__tag">Co-applicant employer</span>
                <div className="pd6-field-row mt-3">
                  <Form.Group className="pd6-field" controlId="stepCoApplicantBusinessName">
                    <Form.Label>Business name</Form.Label>
                    <Form.Control
                      type="text"
                      value={coApplicantBusinessName}
                      onChange={(e) => setCoApplicantBusinessName(e.target.value)}
                    />
                  </Form.Group>
                  <Form.Group className="pd6-field" controlId="stepCoApplicantGrossIncome">
                    <Form.Label>Gross monthly income</Form.Label>
                    <div className="pd6-currency-input">
                      <span className="pd6-currency-input__prefix">$</span>
                      <Form.Control
                        type="text"
                        placeholder="0"
                        value={coApplicantGrossMonthlyIncome}
                        onChange={handleCurrencyChange(setCoApplicantGrossMonthlyIncome)}
                      />
                    </div>
                  </Form.Group>
                </div>
                <Form.Group className="pd6-field" controlId="stepCoApplicantEmployerAddress">
                  <CustomAddressAutocomplete
                    label="Employer address"
                    addressSearchQuery={coApplicantEmployerAddress}
                    setAddressSearchQuery={setCoApplicantEmployerAddress}
                    setAddressComponents={setCoApplicantEmployerAddressComponents}
                    addressValidationError={coApplicantEmployerAddressError}
                    setAddressValidationError={setCoApplicantEmployerAddressError}
                    placeholder="employer address"
                    localStorageLabel="coApplicantEmployerComponentsMazo"
                    isRequired={false}
                    onResolvingChange={handleAddressResolvingChange}
                  />
                  <Form.Control
                    type="text"
                    className="pd6-address-unit-input"
                    placeholder="Apt, suite, or unit (optional)"
                    autoComplete="off"
                    value={coApplicantEmployerUnitNumber}
                    onChange={(e) => setCoApplicantEmployerUnitNumber(e.target.value)}
                  />
                </Form.Group>
              </div>
            )}

            {isRetiredIsh(coApplicantEmploymentStatus) && (
              <div className="pd6-reveal-panel">
                <span className="pd6-reveal-panel__tag">Co-applicant retirement</span>
                <div className="pd6-field-row mt-3">
                  <Form.Group className="pd6-field" controlId="stepCoApplicantRetirementIncome">
                    <Form.Label>Retirement income</Form.Label>
                    <div className="pd6-currency-input">
                      <span className="pd6-currency-input__prefix">$</span>
                      <Form.Control
                        type="text"
                        placeholder="0"
                        value={coApplicantRetirementIncome}
                        onChange={handleCurrencyChange(setCoApplicantRetirementIncome)}
                      />
                    </div>
                  </Form.Group>
                </div>
                <div className="pd6-field-row">
                  <Form.Group className="pd6-field" controlId="stepCoApplicantRetirementYears">
                    <Form.Label>Years</Form.Label>
                    <Form.Control
                      type="text"
                      value={coApplicantRetirementYears}
                      onChange={(e) => setCoApplicantRetirementYears(e.target.value)}
                    />
                  </Form.Group>
                  <Form.Group className="pd6-field" controlId="stepCoApplicantRetirementMonths">
                    <Form.Label>Months</Form.Label>
                    <Form.Control
                      type="text"
                      value={coApplicantRetirementMonths}
                      onChange={(e) => setCoApplicantRetirementMonths(e.target.value)}
                    />
                  </Form.Group>
                </div>
              </div>
            )}

            <div className="pd6-field-row">
              <Form.Group className="pd6-field" controlId="stepCoApplicantEmploymentYears">
                <Form.Label>Years employed</Form.Label>
                <Form.Control
                  type="text"
                  value={coApplicantEmploymentYears}
                  onChange={(e) => setCoApplicantEmploymentYears(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="pd6-field" controlId="stepCoApplicantEmploymentMonths">
                <Form.Label>Months employed</Form.Label>
                <Form.Control
                  type="text"
                  value={coApplicantEmploymentMonths}
                  onChange={(e) => setCoApplicantEmploymentMonths(e.target.value)}
                />
              </Form.Group>
            </div>

            <Form.Group className="pd6-field" controlId="stepCoApplicantAdditionalIncome">
              <Form.Label>Other income</Form.Label>
              <div className="pd6-chip-group">
                {ADDITIONAL_INCOME_SOURCE_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`pd6-chip ${
                      coApplicantAdditionalIncomeSource === option ? "pd6-chip--selected" : ""
                    }`}
                    onClick={() => setCoApplicantAdditionalIncomeSource(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
              <div className="pd6-currency-input" style={{ marginTop: "8px" }}>
                <span className="pd6-currency-input__prefix">$</span>
                <Form.Control
                  type="text"
                  placeholder="0"
                  value={coApplicantAdditionalMonthlyIncome}
                  onChange={handleCurrencyChange(setCoApplicantAdditionalMonthlyIncome)}
                />
              </div>
            </Form.Group>
          </div>
        )}

        {serverError && <div className="pd6-field-error mb-3">{serverError}</div>}

        {submitAttempted && Object.keys(errors).length > 0 && (
          <div className="pd6-form-summary-error">
            {Object.keys(errors).length} field{Object.keys(errors).length === 1 ? "" : "s"} still
            need{Object.keys(errors).length === 1 ? "s" : ""} an answer.
          </div>
        )}

        <div className="pd6-step__nav">
          <div className="pd6-step__nav-buttons">
            <button
              type="button"
              className="pd6-btn pd6-btn--ghost"
              onClick={goBack}
              disabled={showLoader || isFirstStep}
            >
              <span className="pd6-btn__arrow">←</span>
              <span>Back</span>
            </button>
            <button
              type="submit"
              className="pd6-btn pd6-btn--primary"
              disabled={showLoader || pendingAddressLookups > 0}
            >
              {showLoader ? (
                <Spinner style={{ width: "1.2rem", height: "1.2rem" }} />
              ) : (
                <>
                  <span>Continue</span>
                  <span className="pd6-btn__arrow">→</span>
                </>
              )}
            </button>
          </div>
          {footNote && <span className="pd6-step__foot-note">{footNote}</span>}
        </div>
      </Form>
    </div>
  );
});

export default StepEmployment;
